<template>
  <a
    class="related-guide-card-link"
    :href="link"
    target="_blank"
    @click="handleGuideClick"
  >
    <div class="related-guide-card">
      <img
        class="related-guide-card__avatar"
        :src="avatarLink"
        :alt="fullDefinition"
      />

      <div class="related-guide-card__info">
        <div class="related-guide-card-info__name">{{ name }}</div>
        <div class="related-guide-card-info__definition">{{ definition }}</div>
        <div class="related-guide-card-info__rating-and-reviews">
          <div
            v-if="reviews"
            class="related-guide-card-info__rating"
          >
            <rating
              hint="Рейтинг гида"
              one-star
              :size="16"
              :value="rating"
            />
            <span>{{ formattedRating }}</span>
          </div>

          <span class="related-guide-card-info__reviews">{{ formattedReviews }}</span>
        </div>
      </div>

      <div class="related-guide-card__description">
        <ArticleContentRender :content="description" />
      </div>
    </div>
  </a>
</template>

<script lang="ts" setup>
  import { computed } from 'vue';
  import { ArticleContentRender } from '@/features/articleContent';
  import type { ArticleContent } from '@/features/articleContent';
  import { pluralize } from '@/shared/utils/language';

  /** Заглушка рейтинга до подключения общего UI-компонента. */
  const Rating = {
    name: 'Rating',
    props: ['hint', 'oneStar', 'size', 'value'],
    template: '<span>⭐</span>',
  };

  export interface Props {
    id: number;
    name: string;
    avatarLink: string;
    determination?: string;
    location: string;
    rating: number;
    reviews: number;
    description: ArticleContent;
  }

  const props = defineProps<Props>();

  const emit = defineEmits<{
    (e: 'guide-clicked', guideId: number): void;
  }>();

  const link = computed(() => `/guide/${props.id}/`);
  const handleGuideClick = () => emit('guide-clicked', props.id);
  const definition = computed(() => {
    const def = props.determination ? props.determination : `Гид ${props.location}`;

    return def.length ? `${def[0].toLocaleUpperCase()}${def.slice(1)}` : '';
  });

  const fullDefinition = computed(() => `${props.name} ${definition.value}`);
  const formattedRating = computed(() => props.rating.toFixed(1));
  const formattedReviews = computed(() =>
    props.reviews ? `${props.reviews} ${pluralize(props.reviews, ['отзыв', 'отзыва', 'отзывов'])}` : 'Ждет отзывов',
  );
</script>

<style lang="scss" scoped>
  .related-guide-card-link {
    color: var(--global-color-text-default);
    text-decoration: none;

    &:hover,
    &:active {
      text-decoration: none;
    }
  }

  .related-guide-card {
    display: grid;
    grid-template-columns: 96px 1fr;
    box-sizing: border-box;
    max-width: 600px;
    column-gap: var(--alias-space-12);

    @include mobile {
      grid-template-columns: 80px 1fr;
    }
  }

  .related-guide-card__avatar {
    display: block;
    grid-row: span 3;
    object-fit: cover;
    box-sizing: border-box;
    width: 96px;
    height: 136px;
    border: 1px solid var(--global-color-transparent-black-a050);
    border-radius: var(--alias-radius-8);

    @include mobile {
      width: 80px;
      height: 112px;
    }
  }

  .related-guide-card__info {
    @include base-300-300-regular;
  }

  .related-guide-card-info__name {
    @include base-400-300-semi;
  }

  .related-guide-card-info__definition,
  .related-guide-card-info__rating-and-reviews {
    margin-top: var(--alias-space-2);
  }

  .related-guide-card-info__definition {
    overflow: hidden;
    color: var(--alias-color-text-subtle);
    text-overflow: ellipsis;
  }

  .related-guide-card-info__rating-and-reviews {
    display: flex;
    gap: var(--alias-space-6);
    align-items: center;
  }

  .related-guide-card-info__rating {
    display: inline-flex;
    gap: var(--alias-space-2);
    align-items: center;

    :deep(.rating) {
      .icon {
        fill: var(--alias-color-icon-default);
      }
    }
  }

  .related-guide-card-info__reviews {
    color: var(--alias-color-text-subtle);
  }

  .related-guide-card__description {
    & > *:first-child {
      margin-top: 0;
      padding-top: 0;
    }

    & > * {
      @include base-400-300-regular;

      @include mobile {
        @include base-300-300-regular;
      }
    }

    @include mobile {
      grid-column: span 2;
    }
  }
</style>
